import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { motion, useInView } from "framer-motion";
import { CheckCircle2, Shield, Clock, Zap } from "lucide-react";
import { Footer } from "@/components/utilities/Footer";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";

const PricingPage = () => {
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>("pro");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const plansRef = useRef(null);
  const isInView = useInView(plansRef, { once: true, amount: 0.2 });
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Plans data with psychological triggers embedded
  const plans = [
    {
      id: "pro",
      name: "Pro",
      description: "Premium solution for growing teams",
      price: billingCycle === "monthly" ? "$25" : "$250",
      period: billingCycle === "monthly" ? "/month" : "/year",
      savings: billingCycle === "annual" ? "You save $50" : "",
      popular: true,
      accent: "from-emerald-500/20 to-teal-700/20",
      borderAccent: theme === 'dark' ? "border-emerald-700/40" : "border-emerald-600/30",
      icon: Zap,
      features: [
        "1 user included",
        "$15/month per additional user",
        "Unlimited projects",
        "Unlimited repositories",
        "20,000 files limit",
        "Advanced documentation templates",
        "API reference generation",
        "Priority support",
        "30-day version history",
        "14-day free trial",
      ],
      buttonText: "Start 14-day Free Trial",
      buttonStyle: "bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-0 hover:opacity-100 hover:bg-transparent hover:shadow-[0_0_25px_rgba(16,185,129,0.3)] hover:scale-105 transition-all duration-300",
    },
    {
      id: "enterprise",
      name: "Enterprise",
      description: "Ultimate flexibility & control",
      price: "Custom",
      period: "pricing",
      savings: "",
      accent: "from-violet-500/20 to-purple-700/20",
      borderAccent: theme === 'dark' ? "border-violet-700/30" : "border-violet-600/20",
      icon: Shield,
      features: [
        "Unlimited users",
        "Unlimited projects",
        "Unlimited repositories",
        "Unlimited files",
        "Custom integrations",
        "Private hosting options",
        "SSO authentication",
        "Dedicated account manager",
        "24/7 priority support",
        "Unlimited version history",
        "Custom branding options",
      ],
      buttonText: "Schedule a Call",
      buttonStyle: theme === 'dark' 
        ? "bg-violet-600/20 text-violet-300 hover:bg-transparent border border-violet-700/50 hover:scale-105 transition-all duration-300 hover:shadow-lg"
        : "bg-violet-600/10 text-violet-700 hover:bg-transparent border border-violet-600/30 hover:scale-105 transition-all duration-300 hover:shadow-lg",
    },
  ];

  const toggleBillingCycle = () => {
    setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly");
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const planVariants = {
    initial: { scale: 1, y: 0 },
    hover: {
      scale: 1.02,
      y: -5,
      transition: { type: "spring", stiffness: 400, damping: 20 },
    },
    selected: {
      scale: 1.03,
      y: -8,
      transition: { type: "spring", stiffness: 400, damping: 20 },
    },
  };

  const featureVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06,
      },
    },
  };

  const featureItem = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 100, damping: 10 },
    },
  };

  // Determine if a plan is selected
  const getPlanVariant = (planId: string) => {
    if (planId === selectedPlan) return "selected";
    if (planId === hoveredPlan) return "hover";
    return "initial";
  };

  // Handle button clicks
  const handleButtonClick = (planId: string) => {
    if (planId === "pro") {
      // Redirect to login page for Pro plan
      navigate("/auth/login");
    } else {
      // Open scheduling link for Enterprise plan
      window.open("https://cal.com/aniket-docgen/30min", "_blank");
    }
  };
  
  // Define text color class based on theme
  const textColorClass = theme === 'dark' ? 'text-white' : 'text-gray-800';

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#030405]' : 'bg-background'} ${textColorClass} overflow-hidden relative flex flex-col`}>
      {/* Background gradient elements */}
      <div className="absolute pointer-events-none inset-0 z-0 overflow-hidden">
        {/* Grid pattern for texture */}
        <div className={`absolute inset-0 ${theme === 'dark'
          ? 'bg-[linear-gradient(to_right,#4f4f4f25_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f25_1px,transparent_1px)]' 
          : 'light-grid-pattern'} bg-[size:15px_15px] opacity-40`} />
          
        {/* Gradient orbs */}
        <motion.div
          className={`absolute bottom-1/3 -right-40 w-[550px] h-[550px] rounded-full opacity-30 blur-3xl ${theme === 'dark' 
            ? 'bg-gradient-to-br from-primary/20 via-primary/25 to-transparent'
            : 'bg-gradient-to-br from-emerald-500/15 via-primary-light/20 to-transparent'}`}
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        <motion.div
          className={`absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl ${theme === 'dark'
            ? 'bg-gradient-to-br from-primary/20 via-primary/25 to-transparent'
            : 'bg-gradient-to-br from-emerald-600/20 via-emerald-500/25 to-transparent'}`}
          animate={{
            y: [0, 20, 0],
            x: [0, -20, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>
      
      <div className="container relative z-10 mx-auto px-4 py-16 lg:py-24 flex-grow">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.h1
            variants={itemVariants}
            className={`text-4xl sm:text-5xl font-bold mb-6 ${theme === 'dark' 
              ? 'text-transparent bg-gradient-to-r from-white to-gray-400 bg-clip-text'
              : 'text-transparent bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text'}`}
          >
            Simple, Transparent Pricing
          </motion.h1>
          
          <motion.p
            variants={itemVariants}
            className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-10`}
          >
            Choose the plan that fits your documentation needs, with flexible options for teams of all sizes
          </motion.p>
          
          <motion.div
            className={`inline-flex items-center p-1 rounded-xl ${theme === 'dark' 
              ? 'bg-[#0D1117]/40 backdrop-blur-sm border border-slate-700/60' 
              : 'bg-gray-200/50 backdrop-blur-sm border border-gray-300/50'} mb-2`}
            variants={itemVariants}
          >
            <button
              onClick={toggleBillingCycle}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                billingCycle === "monthly"
                  ? theme === 'dark' 
                    ? 'bg-[#0D1117]/70 text-white shadow-lg' 
                    : 'bg-white/90 text-gray-800 shadow-sm'
                  : theme === 'dark' 
                    ? 'text-gray-400 hover:text-gray-300' 
                    : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={toggleBillingCycle}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                billingCycle === "annual"
                  ? theme === 'dark' 
                    ? 'bg-[#0D1117]/70 text-white shadow-lg' 
                    : 'bg-white/90 text-gray-800 shadow-sm'
                  : theme === 'dark' 
                    ? 'text-gray-400 hover:text-gray-300' 
                    : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual <span className={`${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-500'} text-xs`}>Save 16%</span>
            </button>
          </motion.div>
        </motion.div>

        {/* Pricing Plans */}
        <div 
          ref={plansRef}
          className="grid gap-6 lg:gap-8 md:grid-cols-2 mt-16 max-w-4xl mx-auto"
        >
          {plans.map((plan) => (
            <motion.div
              key={plan.id}
              initial={isInView ? "visible" : "hidden"} 
              animate={isInView ? "visible" : "hidden"} 
              variants={itemVariants}
              whileHover="hover"
              custom={parseInt(plan.id, 10)}
              className="h-full"
              onHoverStart={() => setHoveredPlan(plan.id)}
              onHoverEnd={() => setHoveredPlan(null)}
            >
              <motion.div 
                className={`relative h-full rounded-2xl bg-gradient-to-b ${plan.accent} p-[1px] transition-all duration-300`}
                variants={planVariants}
                animate={getPlanVariant(plan.id)}
              >
                {/* Popular badge */}
                {plan.popular && (
                  <div className="absolute -top-5 inset-x-0 flex justify-center">
                    <span className={`${theme === 'dark' 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-emerald-100 text-emerald-700'} px-3 py-1 text-xs font-medium rounded-full shadow-sm`}>
                      Most Popular
                    </span>
                  </div>
                )}

                <div className={`h-full rounded-2xl ${theme === 'dark' 
                  ? 'bg-[#0D1117]/90 backdrop-blur-sm' 
                  : 'bg-white/95 backdrop-blur-sm'} ${plan.borderAccent}`}>
                  <div className="px-6 py-8 flex flex-col h-full">
                    {/* Plan header */}
                    <div className="flex-grow flex flex-col">
                      <div className="flex items-center mb-4">
                        <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-[#101622]/60' : 'bg-gray-100'} mr-3`}>
                          <plan.icon className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />
                        </div>
                        <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{plan.name}</h3>
                      </div>
                      <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-6 text-sm`}>{plan.description}</p>

                      {/* Pricing */}
                      <div className="mb-6">
                        <div className="flex items-baseline">
                          <span className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{plan.price}</span>
                          <span className={`ml-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {plan.period}
                          </span>
                        </div>
                        {plan.id === "pro" && (
                          <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                            Base price for 1 user
                            <span className={`${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-500'} ml-1`}>
                              ($15 per additional user)
                            </span>
                          </div>
                        )}
                        {plan.savings && (
                          <div className={`text-xs ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-500'} mt-1 font-medium`}>
                            {plan.savings}
                          </div>
                        )}
                      </div>

                      {/* Features */}
                      <motion.div
                        variants={featureVariants}
                        className="mb-8"
                      >
                        <h4 className={`text-sm font-semibold mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          What's included:
                        </h4>
                        <ul className="space-y-2">
                          {plan.features.map((feature, idx) => (
                            <motion.li
                              key={idx}
                              variants={featureItem}
                              className={`flex items-start ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} text-sm`}
                            >
                              <CheckCircle2 className={`h-4 w-4 mr-2 flex-shrink-0 mt-0.5 ${theme === 'dark' ? 'text-emerald-500' : 'text-emerald-500'}`} />
                              <span>{feature}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </motion.div>
                    </div>

                    {/* Button - positioned at the bottom */}
                    <Button
                      className={`w-full py-5 rounded-xl mt-auto ${plan.buttonStyle}`}
                      onClick={() => handleButtonClick(plan.id)}
                    >
                      {plan.buttonText}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-12 max-w-2xl mx-auto"
          variants={itemVariants}
        >
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'} mb-4`}>
            Need a custom solution?
          </h2>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
            Our team offers personalized plans to meet your specific documentation needs.
            Schedule a call to discuss your requirements and get a tailored solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className={`${theme === 'dark' 
                ? 'bg-gradient-to-r from-emerald-600 to-emerald-700' 
                : 'bg-gradient-to-r from-emerald-500 to-emerald-600'} text-white px-6 py-2.5 rounded-lg font-medium shadow-lg hover:opacity-100 hover:bg-transparent transition-all duration-300 transform hover:scale-105 ${theme === 'dark' ? 'hover:shadow-[0_0_25px_rgba(16,185,129,0.3)]' : 'hover:shadow-[0_0_25px_rgba(16,185,129,0.3)]'}`}
              onClick={() => window.open("https://cal.com/aniket-docgen/30min", "_blank")}
            >
              Schedule a Call
            </Button>
            <Button
              className={`bg-transparent border border-gray-400 ${theme === 'dark' 
                ? 'text-gray-300 hover:bg-gray-800/30' 
                : 'text-gray-600 hover:bg-gray-100/30'} px-6 py-2.5 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:border-gray-500`}
              onClick={() => window.location.href = "mailto:aniket@docgen.dev?subject=Custom Pricing Inquiry"}
            >
              Email Us
            </Button>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default PricingPage; 